import { Link } from "react-router-dom";
import { 
  Phone, 
  Star, 
  Heart, 
  Shield, 
  Users, 
  ArrowRight,
  CheckCircle,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HomePage = () => {
  const reassuranceItems = [
    {
      icon: Heart,
      title: "Approche sans jugement",
      description: "Nous comprenons vos inquiétudes et vous accompagnons avec bienveillance."
    },
    {
      icon: Shield,
      title: "Environnement apaisant",
      description: "Un espace calme et accueillant conçu pour votre confort."
    },
    {
      icon: Sparkles,
      title: "Expertise complète",
      description: "Prothèses complètes, partielles et sur implants pour une solution adaptée à vos besoins."
    }
  ];

  return (
    <div className="bg-[#F5F5F0]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="container-custom section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                <span className="text-sm font-medium text-slate-700">
                  Note de 5,0 étoiles
                </span>
              </div>

              <h1 className="heading-playfair heading-xl text-slate-900 leading-tight">
                Denturologiste à Saint-Bruno
              </h1>
              <p className="heading-playfair text-xl md:text-2xl text-slate-700 -mt-4">
                Prothèses complètes, partielles et sur implants réalisées avec précision et respect.
              </p>

              <p className="text-lato text-body-lg max-w-xl">
                Chez Sourires Saint-Bruno Denturologistes, nous offrons un accompagnement professionnel, humain et structuré pour des patients qui recherchent un service de qualité et une relation de confiance.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="hero-cta-appointment">
                  <Button className="btn-primary text-base px-8 py-6 w-full sm:w-auto">
                    Prendre rendez-vous
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <a 
                  href="tel:450-441-3636" 
                  className="btn-call text-base w-full sm:w-auto justify-center"
                  data-testid="hero-cta-call"
                >
                  <Phone className="w-5 h-5" />
                  <span>450-441-3636</span>
                </a>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative animate-fade-in-up delay-200">
              <div className="hero-image aspect-[4/3] lg:aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_sourires-precision/artifacts/kt184ma6_0FDD6204-37D1-4278-BA64-EBE2EB44A0AE.PNG"
                  alt="Marcela Chiri Valdivia conseillant un patient"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white rounded-xl shadow-lg p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-slate-700">
                    Service attentionné
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notre approche Section */}
      <section className="bg-white section-padding" data-testid="reassurance-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-playfair heading-lg text-slate-900 mb-8 text-center">
              Notre approche
            </h2>
            
            <div className="space-y-6 text-center">
              <p className="text-body-lg">
                Nous croyons qu'un traitement bien réalisé commence par une écoute attentive et des explications claires.
              </p>
              <p className="text-body-lg">
                Chaque prothèse est planifiée avec rigueur, conçue avec précision et ajustée avec minutie afin d'assurer confort, stabilité et durabilité.
              </p>
              <p className="text-body-lg font-medium text-slate-900">
                Notre objectif est simple : offrir un travail soigné dans un environnement calme et respectueux.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nos services Section */}
      <section className="section-padding" data-testid="specialization-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image de la salle */}
            <div className="hero-image aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_sourires-precision/artifacts/udca4jzh_3C048F0B-08D8-49CC-8143-DE73D82FF076.PNG"
                alt="Salle de traitement moderne de la clinique Sourires Saint-Bruno"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Contenu */}
            <div className="space-y-8">
              <h2 className="heading-playfair heading-lg text-slate-900">
                Nos services
              </h2>

              <p className="text-body-lg">
                Nous offrons :
              </p>

              <ul className="space-y-4">
                {[
                  "Prothèses dentaires complètes",
                  "Prothèses dentaires partielles",
                  "Prothèses sur implants",
                  "Ajustements et suivis",
                  "Nettoyage et polissage annuel"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-body text-lg">{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/services" data-testid="specialization-cta">
                <Button className="btn-secondary">
                  En savoir plus sur nos services
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi nous choisir Section */}
      <section className="bg-white section-padding" data-testid="why-choose-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-playfair heading-lg text-slate-900 mb-8 text-center">
              Pourquoi nous choisir
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Shield, text: "Approche professionnelle et structurée" },
                { icon: Heart, text: "Service personnalisé" },
                { icon: Users, text: "Suivi attentif" },
                { icon: Star, text: "Note moyenne de 5,0 étoiles" },
                { icon: Sparkles, text: "Clinique établie à Saint-Bruno" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-[#F5F5F0] rounded-xl">
                  <item.icon className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-body font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Localisation */}
            <div className="mt-12 p-6 bg-slate-900 rounded-2xl text-center">
              <p className="text-white text-lg mb-2">
                Située au 1501, place de l'Hôtel-de-Ville à Saint-Bruno-de-Montarville, la clinique accueille les patients de la Rive-Sud de Montréal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="bg-white section-padding" data-testid="about-preview-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="heading-playfair heading-lg text-slate-900">
                Une approche humaine avant tout
              </h2>

              <p className="text-body-lg">
                Marcela Chiri Valdivia, d.d., exerce avec une attention particulière 
                envers les patients anxieux ou ayant vécu des expériences difficiles.
              </p>

              <p className="text-body">
                Son approche repose sur l'écoute active, le respect, la douceur 
                et la précision professionnelle. Chaque patient mérite d'être 
                accompagné avec dignité et bienveillance.
              </p>

              <Link to="/a-propos" data-testid="about-preview-cta">
                <Button className="btn-secondary">
                  Rencontrer Marcela
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="hero-image aspect-square rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_sourires-precision/artifacts/z035whj1_B2F19532-230D-4264-ADC3-7AAAC8C61443.PNG"
                  alt="Marcela Chiri Valdivia, d.d., denturologiste"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 right-4 md:-bottom-6 md:right-8 bg-slate-900 text-white rounded-xl shadow-lg p-4 md:p-6">
                <p className="heading-playfair text-lg font-semibold">
                  Marcela Chiri Valdivia
                </p>
                <p className="text-slate-300 text-sm">d.d., Denturologiste</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-[#D4AF37] fill-[#D4AF37]" />
              ))}
            </div>
            <h2 className="heading-playfair heading-lg text-slate-900 mb-4">
              Ce que nos patients disent
            </h2>
            <p className="text-body-lg max-w-2xl mx-auto">
              Avis vérifiés de nos patients sur Google
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Testimonial 1 - Sylvie L. */}
            <div className="card p-6" data-testid="testimonial-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold">
                  S
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Sylvie L.</p>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-body text-sm leading-relaxed">
                « Une équipe FORMIDABLE, un service exceptionnel de toute l'équipe. 
                Elles sont incroyable et très humaine, elles ont une compréhension 
                et une patience d'or. Je les recommande grandement car elles sont 
                professionnelles jusqu'au bout des doigts. »
              </p>
            </div>

            {/* Testimonial 2 - Francine */}
            <div className="card p-6" data-testid="testimonial-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-semibold">
                  F
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Francine B.</p>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-body text-sm leading-relaxed">
                « Ayant vécu de mauvaises expériences dans le passé, j'appréhendais 
                de devoir refaire une de mes prothèses mais Marcela a su me rassurer 
                et me conseiller de façon professionnelle et rassurante tout au long 
                de la démarche. Je suis entièrement satisfaite. »
              </p>
            </div>

            {/* Testimonial 3 - Melissa */}
            <div className="card p-6" data-testid="testimonial-3">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-semibold">
                  M
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Melissa R.</p>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-body text-sm leading-relaxed">
                « J'ai eu la chance d'avoir Marcela comme denturologue. Un excellent 
                service à l'écoute de ses clients. Toujours souriante. Je l'ai comme 
                denturologue depuis bientôt 5 ans et je ne changerais pas même pour 
                tout l'or du monde. »
              </p>
            </div>

            {/* Testimonial 4 - Valérie */}
            <div className="card p-6" data-testid="testimonial-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-semibold">
                  V
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Valérie F.</p>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-body text-sm leading-relaxed">
                « J'ai vu Marcela à quelques reprises et son service est super ! 
                De bons soins et conseils reçus de sa part. Elle est formidable. 
                Merci Marcela »
              </p>
            </div>

            {/* Testimonial 5 */}
            <div className="card p-6 md:col-span-2 lg:col-span-2" data-testid="testimonial-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-semibold">
                  O
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Optimus P.</p>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-body text-sm leading-relaxed">
                « Marcela est une personne attentionnée et a répondu à tous mes besoins. 
                Si vous recherchez des super belles dents, c'est la personne à aller rencontrer. 
                Merci encore pour tout ce que tu as fait pour moi »
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="section-padding" data-testid="social-proof-section">
        <div className="container-custom">
          <div className="bg-slate-900 rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <h2 className="heading-playfair text-2xl md:text-3xl text-white mb-6">
              Prenez rendez-vous dès aujourd'hui
            </h2>
            <a 
              href="tel:450-441-3636" 
              className="inline-flex items-center gap-3 btn-call text-xl px-10 py-6"
              data-testid="main-cta-call"
            >
              <Phone className="w-6 h-6" />
              <span>450-441-3636</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white section-padding" data-testid="final-cta-section">
        <div className="container-custom text-center">
          <Users className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" />
          <h2 className="heading-playfair heading-lg text-slate-900 mb-4">
            Prêt à retrouver le plaisir de sourire?
          </h2>
          <p className="text-body-lg max-w-2xl mx-auto mb-8">
            Appelez-nous dès aujourd'hui pour une consultation personnalisée. 
            Nous sommes là pour répondre à toutes vos questions.
          </p>
          <a 
            href="tel:450-441-3636" 
            className="btn-primary text-lg px-10 py-6 inline-flex"
            data-testid="final-cta-call"
          >
            <Phone className="w-5 h-5" />
            <span>450-441-3636</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
